import { StoredTabManagerState, TabManagerStorateAdapter } from './types';

export class UrlTabManagerStorageAdapter implements TabManagerStorateAdapter {
  private readonly listeners = new Set<
    (state: StoredTabManagerState | null) => void
  >();
  private lastKnownState: StoredTabManagerState | null = null;

  constructor(
    private readonly queryKey: string = 'tabs',
    private readonly url: {
      location: { search: string; href: string };
      addEventListener: (
        key: 'popstate',
        cb: (ev: PopStateEvent) => void
      ) => void;
      removeEventListener: (
        key: 'popstate',
        cb: (ev: PopStateEvent) => void
      ) => void;
      history: {
        pushState: (
          data: any,
          unused: string,
          url?: string | URL | null
        ) => void;
      };
    } = window
  ) {
    this.handlePopstate.bind(this);

    // Start listening for popstate events immediately
    this.startListening();

    // Initialize the last known state
    this.lastKnownState = this.parseTabsFromQuery();
  }

  private startListening(): void {
    this.url.addEventListener('popstate', this.handlePopstate);
  }

  private stopListening(): void {
    this.url.removeEventListener('popstate', this.handlePopstate);
  }

  private handlePopstate = () => {
    const currentState = this.parseTabsFromQuery();

    // Only notify if the tabs-related query parameters actually changed
    if (this.hasStateChanged(this.lastKnownState, currentState)) {
      this.lastKnownState = currentState;
      this.notify();
    }
  };

  private hasStateChanged(
    oldState: StoredTabManagerState | null,
    newState: StoredTabManagerState
  ): boolean {
    if (!oldState) {
      return true;
    }

    return oldState.activeTab !== newState.activeTab;
  }

  notify(): void {
    for (const listener of this.listeners) {
      listener(this.lastKnownState);
    }
  }

  subscribe(
    callback: (state: StoredTabManagerState | null) => void
  ): () => void {
    this.listeners.add(callback);

    return () => {
      this.listeners.delete(callback);
    };
  }

  getState(): StoredTabManagerState {
    return this.parseTabsFromQuery();
  }

  //sets the "state" url
  setState(state: StoredTabManagerState): void {
    if (
      this.lastKnownState !== null &&
      !this.hasStateChanged(state, this.lastKnownState)
    ) {
      return;
    }

    const url = new URL(this.url.location.href);
    const tabsQuery = this.buildTabsQuery(state);

    if (tabsQuery) {
      url.searchParams.set(this.queryKey, tabsQuery);
    } else {
      url.searchParams.delete(this.queryKey);
    }

    // Update our internal state before pushing to history
    this.lastKnownState = state;
    this.url.history.pushState(null, '', url.toString());
  }

  // Clean up method - call this when the adapter is no longer needed
  destroy(): void {
    this.stopListening();
    this.listeners.clear();
  }

  private parseTabsFromQuery(): StoredTabManagerState {
    // Use window.location.search instead of the injected url for real-time updates
    const searchParams = this.url.location.search;
    const urlParams = new URLSearchParams(searchParams);
    const tabsParam = urlParams.get(this.queryKey);

    if (!tabsParam) {
      return {
        activeTab: null,
        tabs: [],
      };
    }

    const parts = tabsParam.split(';');
    let activeTab: string | null = null;
    const tabs = new Set<string>();

    for (const part of parts) {
      if (part.startsWith(`${this.queryKey}.open=`)) {
        const tabValue = part.replace(`${this.queryKey}.open=`, '');
        const newTabs = tabValue.split(',');
        if (newTabs.length > 0) {
          newTabs.forEach((tab) => tabs.add(tab));
        }
      } else if (part.startsWith(`${this.queryKey}.active=`)) {
        const activeValue = part.replace(`${this.queryKey}.active=`, '');
        tabs.add(activeValue);
        if (activeValue) {
          activeTab = activeValue;
        }
      }
    }

    return { activeTab, tabs: Array.from(tabs) };
  }

  private buildTabsQuery(state: StoredTabManagerState): string {
    const parts: string[] = [];

    if (state.activeTab) {
      parts.push(`${this.queryKey}.active=${state.activeTab}`);
    }

    return parts.join(';');
  }
}
